import type React from 'react';
import { colors } from '@this/shared/ui/theme/tokens/primitive-tokens/colors';
import { getCommonStyles } from '@this/components/online_applications/shared/commonStyles';

// Admin用スタイルをcommonStylesを拡張して定義
function getAdminStyles() {
  const commonStyles = getCommonStyles();

  return {
    ...commonStyles,

    // Admin固有のスタイルを追加・上書き
    section: {
      ...commonStyles.section,
      card: {
        backgroundColor: colors.gray[20],
        borderRadius: '8px',
        padding: '16px'
      } as React.CSSProperties
    },

    // フィルター関連
    filter: {
      grid: {
        display: 'grid',
        gridTemplateColumns: 'minmax(250px, 1fr) 150px 280px 280px auto',
        gap: '12px',
        marginBottom: '8px'
      } as React.CSSProperties
    },

    // アクションボタン
    actions: {
      container: {
        display: 'flex',
        gap: '8px',
        justifyContent: 'flex-end'
      } as React.CSSProperties,
      button: {
        padding: '6px 12px',
        fontSize: '14px'
      } as React.CSSProperties
    },

    // 詳細ビュー関連
    detail: {
      header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      } as React.CSSProperties
    },

    // フォーム関連（Admin編集用）
    form: {
      container: {
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '24px'
      } as React.CSSProperties,
      section: {
        marginBottom: '24px',
        paddingBottom: '24px',
        borderBottom: `1px solid ${colors.gray[50]}`
      } as React.CSSProperties,
      actions: {
        display: 'flex',
        gap: '12px',
        justifyContent: 'flex-end',
        marginTop: '32px',
        paddingTop: '24px',
        borderBottom: `1px solid ${colors.gray[50]}`
      } as React.CSSProperties
    },

    // 説明文
    description: {
      fontSize: '14px',
      color: colors.gray[600],
      lineHeight: 1.6
    } as React.CSSProperties,

    // ヘルプテキスト
    help: {
      fontSize: '12px',
      color: colors.gray[500],
      marginTop: '4px'
    } as React.CSSProperties,

    // ラジオグループ
    radioGroup: {
      horizontal: {
        display: 'flex',
        gap: '16px',
        alignItems: 'center'
      } as React.CSSProperties,
      label: {
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer'
      } as React.CSSProperties
    },

    // チェックボックス
    checkbox: {
      label: {
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer'
      } as React.CSSProperties
    },

    // サイドバーナビゲーション
    sidebar: {
      container: {
        width: '160px',
        backgroundColor: colors.gray[20],
        borderRight: `1px solid ${colors.gray[50]}`,
        padding: '20px 0',
        height: '100vh',
        position: 'sticky' as const,
        top: 0,
        overflowY: 'auto' as const
      } as React.CSSProperties,
      list: {
        listStyle: 'none',
        padding: 0,
        margin: 0
      } as React.CSSProperties,
      listItem: {
        marginBottom: '4px'
      } as React.CSSProperties,
      button: {
        width: '100%',
        padding: '12px 20px',
        border: 'none',
        backgroundColor: 'transparent',
        color: colors.black,
        textAlign: 'left' as const,
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: 'normal'
        // transition: 'all 0.2s ease'
      } as React.CSSProperties,
      buttonActive: {
        fontSize: '14px'
      } as React.CSSProperties
    },

    // フィールド関連（commonStylesを拡張）
    field: {
      ...commonStyles.field,
      group: {
        paddingBottom: '16px'
      } as React.CSSProperties
    },

    // グリッドレイアウト（commonStylesを拡張）
    grid: {
      ...commonStyles.grid,
      two: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '16px'
      } as React.CSSProperties,
      threeColumn: {
        display: 'grid',
        gridTemplateColumns: '3fr auto auto',
        gap: '12px',
        alignItems: 'center'
      } as React.CSSProperties,
      fourColumn: {
        display: 'grid',
        gridTemplateColumns: 'auto 140px 50px 30px',
        gap: '12px',
        alignItems: 'center'
      } as React.CSSProperties
    },

    // 見出し関連（commonStylesを拡張）
    heading: {
      ...commonStyles.heading,
      small: {
        margin: '0 0 8px 0',
        fontSize: '12px',
        fontWeight: 'bold',
        textTransform: 'uppercase' as const
      } as React.CSSProperties
    },

    // タイトル関連
    title: {
      ...commonStyles.title,
      fullWidth: {
        ...commonStyles.title.fullWidth,
        padding: '20px 0',
        borderBottom: '1px solid #eee'
      } as React.CSSProperties
    },

    // 固定フッター関連
    fixedFooter: {
      ...commonStyles.fixedFooter,
      rightAlign: {
        position: 'fixed' as const,
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: colors.white,
        borderTop: `1px solid ${colors.gray[100]}`,
        padding: '16px 20px',
        display: 'flex',
        justifyContent: 'flex-end',
        zIndex: 1000
      } as React.CSSProperties
    },

    // プレビュー用入力フィールド
    previewInput: {
      base: {
        width: '100%',
        maxWidth: '600px',
        cursor: 'default',
        backgroundColor: colors.white
      } as React.CSSProperties
    },

    inputProps: {
      price: {
        min: 0,
        style: { textAlign: 'right' as const }
      }
    },

    // 削除ボタン
    deleteButton: {
      base: {
        width: '20px',
        height: '20px',
        padding: 0,
        borderRadius: '50%',
        backgroundColor: colors.red[500],
        color: colors.white,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0
      } as React.CSSProperties,
      hover: {
        backgroundColor: colors.red[800]
      } as React.CSSProperties,
      normal: {
        backgroundColor: colors.red[500]
      } as React.CSSProperties
    },

    // エンコーディングセレクター
    encodingSelector: {
      margin: 0,
      height: '30px',
      padding: '0 12px',
      borderRadius: '4px',
      cursor: 'pointer'
    } as React.CSSProperties,

    // 日付範囲選択
    dateRange: {
      container: {
        display: 'flex',
        gap: '4px',
        alignItems: 'center'
      } as React.CSSProperties,
      separator: {
        padding: '0 4px'
      } as React.CSSProperties
    },

    // 固定文言表示（オプション追加時の契約条件など）
    fixedText: {
      container: {
        padding: '16px',
        backgroundColor: colors.gray[20],
        border: `1px solid ${colors.gray[100]}`,
        borderRadius: '4px',
        whiteSpace: 'pre-wrap',
        fontSize: '14px',
        lineHeight: '1.8'
      } as React.CSSProperties
    },

    // ページヘッダーアクション
    pageHeaderActions: {
      container: {
        display: 'flex',
        gap: '16px',
        alignItems: 'center',
        marginBottom: '20px'
      } as React.CSSProperties,
      exportGroup: {
        display: 'flex',
        gap: '8px',
        alignItems: 'center'
      } as React.CSSProperties
    }
  };
}

export const adminOnlineApplicationStyles = getAdminStyles();
export const filterStyles = adminOnlineApplicationStyles.filter;
export const detailStyles = adminOnlineApplicationStyles.detail;
export const formStyles = adminOnlineApplicationStyles.form;
